import React, { useEffect, useState } from 'react';
// @ts-ignore
import styles from './ECardNewFilesListItem.module.scss';
import Checkbox from '../../../../Components/Base/Checkbox/Checkbox';
import TreatmentPatientButton from '../../../../Components/Treatment/TreatmentPatientButton';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import LinkButton from '../../../../Components/Button/LinkButton';
import moment from 'moment';
import { useAppContext } from '../../../../Services/AppContext';
import useDocumentationFileTransfer from '../../Hooks/useDocumentationFileTransfer';
import { DocumentationFile, User } from '../../../../graphql/graphql';
import Text from '../../../../Components/Base/Text/Text';
import { ECardNewFileSelectedItem } from './ECardNewFilesListWrapper';
import { ECardType } from '../../Enums/ECardType.enum';
import MediaViewerModal from '../../../../Components/MediaViewer/MediaViewerModal';
import { prepareDocumentationFileForMediaViewer } from '../../Services/eCardFile.service';
import ECardMoveFileToHealthDocumentationModal from '../ECardMoveFileToHealthDocumentation/ECardMoveFileToHealthDocumentationModal';
import useTranslation from '../../../../Hooks/useTranslation';
import useECardNewFiles from '../../Hooks/useECardNewFiles';
import { UserRelation } from '../../../User/Enums/UserRelation.enum';
import useNotification from '../../../../Hooks/useNotification.hook';
import usePatientIdentification from '../../../Patient/Hooks/usePatientIdentification';
import MultiMediaViewerModal from '../../../Base/Components/MultiMediaViewer/MultiMediaViewerModal';
import { IMultiMediaViewerItem } from '../../../Base/Components/MultiMediaViewer/MultiMediaViewerItem';

interface Props {
	item: DocumentationFile;
	isSelected: boolean;
	onSelect: (item: ECardNewFileSelectedItem, add: boolean) => void;
	afterFileTransfer: () => void;
}

const ECardNewFilesListItem: React.FC<Props> = ({
	item,
	onSelect,
	isSelected,
	afterFileTransfer,
}) => {
	const [isChecked, setIsChecked] = useState(false);
	const { dateFormatMoment, workplaceId, userData, socketUrl } =
		useAppContext();
	const { t } = useTranslation();
	const { markAsNotSeen } = useECardNewFiles();
	const { transfersRecords, isNotMovedToHealthDocumentation } =
		useDocumentationFileTransfer(item, ECardType.PATIENT_E_CARD);
	const [isFileOpen, setIsFileOpen] = useState(false);
	const [
		isMoveFilesToHealthDocumentationOpen,
		setIsMoveFilesToHealthDocumentationOpen,
	] = useState(false);
	const { showNotificationSuccess } = useNotification();
	const { identification } = usePatientIdentification(item.user as User);
	const { isLatam } = useAppContext();

	useEffect(() => {
		setIsChecked(isSelected);
	}, [isSelected]);

	useEffect(() => {
		if (item.user?.id) {
			onSelect(
				{ id: item.id, patientId: item.user.id, folderId: item.folderId },
				isChecked,
			);
		}
	}, [isChecked]);

	const handleCheckItem = () => {
		setIsChecked(prev => !prev);
	};

	const handleOpenDetail = () => {
		setIsFileOpen(true);
	};

	const handleMoveToHealthDocumentation = () => {
		setIsMoveFilesToHealthDocumentationOpen(true);
	};

	const handleAfterFileTransfer = () => {
		setIsMoveFilesToHealthDocumentationOpen(false);
		showNotificationSuccess({
			content: t(
				'component.fileTransfers.fileTransferToHealthDocumentationSuccess',
			),
		});
		afterFileTransfer();
	};

	const handleMarkItemNotSeen = async () => {
		if (item.user?.id) {
			setIsChecked(false);
			await markAsNotSeen({
				id: item.id,
				patientId: item.user?.id,
				folderId: item.folderId,
			});
		}
		afterFileTransfer();
	};

	const hasGroupFiles = false;

	return (
		<>
			<div className={styles.item}>
				<div className={styles.patient}>
					{item.isSeen ? (
						<div onClick={() => handleMarkItemNotSeen()}>
							<img
								src='/dist/icons/eye.svg'
								alt='eye'
								width='17px'
								style={{ marginRight: '8px', cursor: 'pointer' }}
							/>
						</div>
					) : (
						<Checkbox value={isChecked} onChange={handleCheckItem} />
					)}
					<div className={styles.patientData}>
						<div className={styles.text}>
							{moment(item.createdAt).format(dateFormatMoment + ' HH:mm')}
						</div>
						{item?.virtualUser?.id && (
							<TreatmentPatientButton
								text={item?.virtualUser?.profile?.fullName ?? ''}
								patientId={item?.virtualUser?.id}
								workplaceId={workplaceId}
								loggedUserId={userData.id}
								className={styles.title}
							/>
						)}
						{identification.value && (
							<div className={styles.extra}>
								<div className={styles.name}>
									<span>{t(identification.title)}: </span>
									{identification.value}
								</div>
							</div>
						)}
					</div>
				</div>
				<div className={styles.content}>
					<div>
						<div className={styles.title}>{item.name}</div>
					</div>
					<div className={styles.extra}>
						<div className={styles.name}>
							<span>
								{t('component.eCardNewFilesListItem.insertedToFolder')}:{' '}
							</span>
							{item.folder?.name}
						</div>
					</div>

					{item?.user?.userRelations?.map(
						({ id, relationUser, type }: any, index) => {
							if (
								[
									UserRelation.MEDDI_BABY,
									UserRelation.MEDDI_DIABETES,
									UserRelation.MOU,
								].includes(type)
							) {
								return (
									<div className={styles.extra} key={id}>
										<div className={styles.name}>
											<Text
												tx='base.shared.attendingDoctor'
												inline
												firstUpper
											/>
											{': '}
											{relationUser?.profile?.fullName ?? (
												<Text tx='base.shared.unassigned' inline firstUpper />
											)}
										</div>
									</div>
								);
							}
						},
					)}
					{transfersRecords?.map((transfer, key) => (
						<div className={styles.extra} key={key}>
							<div className={styles.name}>
								{transfer.direction} <span>{transfer.date}</span>
							</div>
						</div>
					))}
					{item.isSeen && (
						<div className={styles.extra}>
							<div className={styles.name}>
								<span>{t('base.seenBy')}: </span>
								{item.isSeenMarkedBy?.profile?.fullName}
							</div>
						</div>
					)}
				</div>
				<div className={styles.controls}>
					<Button
						title='base.preview'
						theme={Theme.GREEN}
						onClick={handleOpenDetail}
					/>
					{isNotMovedToHealthDocumentation && (
						<LinkButton
							title='component.eCard.moveToHealthDocumentation'
							theme={Theme.BLUE}
							className={styles.link}
							onClick={handleMoveToHealthDocumentation}
						/>
					)}
				</div>
			</div>
			{isFileOpen && !hasGroupFiles && (
				<MediaViewerModal
					opened
					onClose={() => setIsFileOpen(false)}
					file={prepareDocumentationFileForMediaViewer(item, socketUrl)}
				/>
			)}
			{isFileOpen && hasGroupFiles && (
				<MultiMediaViewerModal
					files={item.groupFiles as IMultiMediaViewerItem[]}
					isOpen
					onClose={() => setIsFileOpen(false)}
				/>
			)}
			{isMoveFilesToHealthDocumentationOpen && item.user?.id && (
				<ECardMoveFileToHealthDocumentationModal
					opened
					targetPatientId={item.virtualUserId}
					patientId={item.user?.id}
					onClose={() => setIsMoveFilesToHealthDocumentationOpen(false)}
					filesToShare={[
						{
							id: item.id,
							name: item.name ?? '',
							file: item,
						},
					]}
					onSuccess={handleAfterFileTransfer}
					isPatientsConsentRequired={false}
				/>
			)}
		</>
	);
};

export default ECardNewFilesListItem;
